import { useState } from "react";
import { Link } from "@remix-run/react";
import type { MetaFunction } from "@remix-run/node";
import { SEOStructuredData, Breadcrumb } from "~/components/SEOStructuredData";
import { calculatePregnancy, validatePregnancyData } from "~/utils/pregnancyCalculations";
import PregnancyProgress from "~/components/PregnancyProgress";

export const meta: MetaFunction = () => {
  return [
    { title: "预产期计算器 - 孕期管理 - 怀孕周期计算 | 女孩的秘密" },
    { 
      name: "description", 
      content: "女孩的秘密预产期计算器，根据末次月经日期科学计算预产期、孕周、胎儿发育情况。提供准确的怀孕周期管理，孕期注意事项提醒，帮助准妈妈科学管理孕期健康。" 
    },
    { 
      name: "keywords", 
      content: "预产期计算,预产期计算器,怀孕周期计算,孕期管理,孕周计算,胎儿发育,怀孕日期计算,孕期健康,预产期查询,怀孕计算器" 
    },
    { name: "robots", content: "index, follow" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    { name: "author", content: "Girls Secret Team" },
    { name: "language", content: "zh-CN" },
    
    // Open Graph
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://girls-secret.com/pregnancy" },
    { property: "og:title", content: "预产期计算器 - 孕期管理 - 女孩的秘密" },
    { property: "og:description", content: "根据末次月经日期科学计算预产期、孕周、胎儿发育情况，提供专业的孕期健康管理服务。" },
    { property: "og:image", content: "https://girls-secret.com/og-image-pregnancy.jpg" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:locale", content: "zh_CN" },
    { property: "og:site_name", content: "女孩的秘密" },
    
    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "预产期计算器 - 孕期管理 - 女孩的秘密" },
    { name: "twitter:description", content: "根据末次月经日期科学计算预产期、孕周、胎儿发育情况，提供专业的孕期健康管理服务。" },
    { name: "twitter:image", content: "https://girls-secret.com/og-image-pregnancy.jpg" },
    
    // 移动端优化
    { name: "mobile-web-app-capable", content: "yes" },
    { name: "apple-mobile-web-app-capable", content: "yes" },
    { name: "format-detection", content: "telephone=no" },
  ];
};

interface PregnancyData {
  lastPeriodDate: string;
}

interface PregnancyResult {
  dueDate: Date;
  currentWeek: number;
  currentDay: number;
  daysPregnant: number;
  daysRemaining: number;
  trimester: number;
  conceptionDate: Date;
}

export default function Pregnancy() {
  const [formData, setFormData] = useState<PregnancyData>({
    lastPeriodDate: ''
  });
  const [result, setResult] = useState<PregnancyResult | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  
  const breadcrumbs = [
    { name: "首页", url: "/" },
    { name: "预产期计算", url: "/pregnancy", current: true }
  ];

  const handleInputChange = (value: string) => {
    setFormData({ lastPeriodDate: value });
    // 清除错误信息
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleCalculate = () => {
    setIsCalculating(true);
    
    // 验证输入数据
    const validation = validatePregnancyData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      setIsCalculating(false);
      return;
    }

    // 计算预产期
    try {
      const calculationResult = calculatePregnancy(formData);
      setResult(calculationResult);
      setErrors([]);
    } catch (error) {
      setErrors(['计算过程中出现错误，请检查输入数据']);
    }
    
    setIsCalculating(false);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTrimesterInfo = (trimester: number) => {
    switch (trimester) {
      case 1:
        return {
          name: '孕早期',
          period: '1-12周',
          description: '胚胎器官形成的关键时期',
          tips: ['补充叶酸', '避免有害物质', '定期产检', '注意休息']
        };
      case 2:
        return {
          name: '孕中期',
          period: '13-27周',
          description: '相对稳定的时期，胎儿快速发育',
          tips: ['均衡营养', '适量运动', '胎教互动', '监测胎动']
        };
      case 3:
        return {
          name: '孕晚期',
          period: '28-40周',
          description: '胎儿成熟，准备分娩',
          tips: ['准备待产包', '学习分娩知识', '密切监测', '保持心情愉快']
        };
      default:
        return null;
    }
  };

  const getProgressPercentage = (daysPregnant: number) => {
    return Math.min((daysPregnant / 280) * 100, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEOStructuredData type="pregnancy" breadcrumbs={breadcrumbs} />
      {/* Header */}
      <header className="glass-card mx-4 mt-4 p-6">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/" className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">💕</span>
            </Link>
            <h1 className="text-2xl font-bold gradient-text">预产期计算</h1>
          </div>
          <nav className="hidden md:flex space-x-6">
            <Link to="/" className="text-gray-600 hover:text-pink-600 transition-colors">
              首页
            </Link>
            <Link to="/cycle" className="text-gray-600 hover:text-pink-600 transition-colors">
              周期管理
            </Link>
            <Link to="/pregnancy" className="text-pink-600 font-medium">
              预产期计算
            </Link>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <Breadcrumb items={breadcrumbs} />
        <div className="grid lg:grid-cols-2 gap-8">
          {/* 输入表单 */}
          <div className="glass-card p-8">
            <h2 className="text-2xl font-bold gradient-text mb-6">输入末次月经信息</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  末次月经开始日期
                </label>
                <input
                  type="date"
                  value={formData.lastPeriodDate}
                  onChange={(e) => handleInputChange(e.target.value)}
                  className="input-field"
                  max={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-gray-500 mt-1">
                  请选择您最后一次月经开始的日期
                </p>
              </div>

              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-red-800 font-medium mb-2">输入错误：</h4>
                  <ul className="text-red-700 text-sm space-y-1">
                    {errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={handleCalculate}
                disabled={isCalculating}
                className={`w-full btn-primary ${isCalculating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isCalculating ? (
                  <span className="flex items-center justify-center">
                    <div className="loading-pulse mr-2">⏳</div>
                    计算中...
                  </span>
                ) : (
                  '计算预产期'
                )}
              </button>

              {/* 计算说明 */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">📋 计算说明</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• 基于Naegele's Rule（内格勒法则）计算</li>
                  <li>• 预产期 = 末次月经日期 + 280天</li>
                  <li>• 正常孕期为37-42周</li>
                  <li>• 仅有约5%的婴儿在预产期当天出生</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 计算结果 */}
          <div className="space-y-6">
            {result && (
              <>
                {/* 孕期进度 */}
                <div className="glass-card p-8 data-load">
                  <h3 className="text-xl font-bold gradient-text mb-6 text-center">孕期进度</h3>
                  <PregnancyProgress result={result} />
                </div>

                {/* 详细信息 */}
                <div className="glass-card p-8 data-load">
                  <h3 className="text-xl font-bold gradient-text mb-6">详细信息</h3>
                  
                  <div className="space-y-4">
                    <div className="bg-pink-50 rounded-lg p-4">
                      <h4 className="font-semibold text-pink-800 mb-2">📅 预产期</h4>
                      <p className="text-pink-700 text-lg font-medium">
                        {formatDate(result.dueDate)}
                      </p>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-800 mb-2">💕 受孕日期（估算）</h4>
                      <p className="text-purple-700">
                        {formatDate(result.conceptionDate)}
                      </p>
                      <p className="text-sm text-purple-600 mt-1">
                        通常在末次月经后14天左右
                      </p>
                    </div>

                    {getTrimesterInfo(result.trimester) && (
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-2">
                          🌱 {getTrimesterInfo(result.trimester)!.name}
                        </h4>
                        <p className="text-green-700 mb-2">
                          {getTrimesterInfo(result.trimester)!.period} - {getTrimesterInfo(result.trimester)!.description}
                        </p>
                        <div className="text-sm text-green-600">
                          <p className="font-medium mb-1">建议事项：</p>
                          <ul className="space-y-1">
                            {getTrimesterInfo(result.trimester)!.tips.map((tip, index) => (
                              <li key={index}>• {tip}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 孕期里程碑 */}
                <div className="glass-card p-8 data-load">
                  <h3 className="text-xl font-bold gradient-text mb-6">孕期里程碑</h3>
                  
                  <div className="space-y-3">
                    <div className={`flex items-center p-3 rounded-lg ${
                      result.currentWeek >= 12 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'
                    }`}>
                      <span className="text-2xl mr-3">{result.currentWeek >= 12 ? '✅' : '⏳'}</span>
                      <div>
                        <div className="font-medium">12周 - 孕早期结束</div>
                        <div className="text-sm">胎儿器官基本形成，流产风险降低</div>
                      </div>
                    </div>

                    <div className={`flex items-center p-3 rounded-lg ${
                      result.currentWeek >= 20 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'
                    }`}>
                      <span className="text-2xl mr-3">{result.currentWeek >= 20 ? '✅' : '⏳'}</span>
                      <div>
                        <div className="font-medium">20周 - 孕期中点</div>
                        <div className="text-sm">可进行大排畸检查，感受胎动</div>
                      </div>
                    </div>

                    <div className={`flex items-center p-3 rounded-lg ${
                      result.currentWeek >= 28 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'
                    }`}>
                      <span className="text-2xl mr-3">{result.currentWeek >= 28 ? '✅' : '⏳'}</span>
                      <div>
                        <div className="font-medium">28周 - 孕晚期开始</div>
                        <div className="text-sm">胎儿存活率大幅提高</div>
                      </div>
                    </div>

                    <div className={`flex items-center p-3 rounded-lg ${
                      result.currentWeek >= 37 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'
                    }`}>
                      <span className="text-2xl mr-3">{result.currentWeek >= 37 ? '✅' : '⏳'}</span>
                      <div>
                        <div className="font-medium">37周 - 足月妊娠</div>
                        <div className="text-sm">胎儿发育成熟，随时可能分娩</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* 注意事项 */}
            <div className="glass-card p-6">
              <h4 className="font-semibold text-gray-800 mb-3">⚠️ 重要提醒</h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• 预产期仅为估算，实际分娩时间可能提前或延后2周</li>
                <li>• 计算基于28天标准周期，个体差异可能影响准确性</li>
                <li>• 请定期进行产前检查，遵循医生建议</li>
                <li>• 如有异常症状，请及时就医</li>
                <li>• 本工具仅供参考，不能替代专业医疗建议</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}